import React, {
    useEffect,
    useContext,
    useState,
    useCallback,
    useReducer,
} from "react";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PlaylistAddOutlinedIcon from "@mui/icons-material/PlaylistAddOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import TitleOutlinedIcon from "@mui/icons-material/TitleOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// My Files
import { DatabaseContext, UserContext } from "../../context";
import ToolbarWithLogo from "../ToolbarWithLogo";
import firebase from "../../firebase";
import ConfirmDialog from "../ConfirmDialog";
import ProjectTitleComponent from "./Components/ProjectTitleComponent";
import ProjectTextBoxComponent from "./Components/ProjectTextBoxComponent";
import ProjectPlaylistComponent from "./Components/ProjectPlaylistComponent";
import ProjectSelectPlaylistDialog from "./ProjectSelectPlaylistDialog";
import ShareProjectDialog from "./ShareProjectDialog";
import ProjectSelectFileDialog from "./ProjectSelectFileDialog";
import ProjectFileComponent from "./Components/ProjectFileComponent";
import NoPermissionsMessage from "../NoPermissionsMessage";
import FloatingAddContentButton from "../FloatingAddContentButton";

const surfaceSx = {
    borderRadius: 3,
    borderColor: "divider",
    backgroundColor: "background.paper",
    boxShadow: "0 8px 28px rgba(20, 35, 25, 0.065)",
};

export default function ProjectEditPage(props) {
    const { currentUserData } = useContext(UserContext);
    const location = useLocation();
    const history = useHistory();

    const {
        projectInfo,
        projectInfoLoaded = false,
        groups,
        groupsLoaded = false,
    } = useContext(DatabaseContext);

    const [title, setTitle] = useState("");
    const [introduction, setIntroduction] = useState("");

    const [key, setKey] = useState("");

    const [project, setProject] = useState();
    const [projectInfoObject, setProjectInfoObject] = useState();

    const [, forceUpdate] = useReducer((x) => x + 1, 0);


    const [shareDialogOpen, setShareDialogOpen] = useState(false);

    const [canShareProjects, setCanShareProjects] = useState(false);

    const [deleteConfirmDialogIsOpen, setDeleteConfirmDialogIsOpen] =
        useState(false);

    const [selectPlaylistDialogOpen, setSelectPlaylistDialogOpen] =
        useState(false);

    const [selectExistingFileDialogOpen, setSelectExistingFileDialogOpen] =
        useState(false);

    const [anchorEl, setAnchorEl] = useState(null);

    const [userHasAccess, setUserHasAccess] = useState(false);
    const [userWasCreator, setUserWasCreator] = useState(false);
    const [accessResolved, setAccessResolved] = useState(false);

    const getProject = useCallback(
        async (key) => {
            let projectRef = firebase.database().ref("projects").child(key);

            if (!project) {
                console.log("Reading Project from Firebase");

                projectRef.on("value", function (snapshot) {
                    let key = snapshot.key;
                    let data = snapshot.val();

                    let componentsArray = [];

                    if (data && data.components) {
                        let componentKeys = Object.keys(data.components);
                        let componentValues = Object.values(data.components);

                        for (let i = 0; i < componentKeys.length; i++) {
                            var commentsArray = [];
                            if (componentValues[i].comments) {
                                let commentKeys = Object.keys(
                                    componentValues[i].comments
                                );
                                let commentValues = Object.values(
                                    componentValues[i].comments
                                );

                                for (let j = 0; j < commentKeys.length; j++) {
                                    const comment = commentValues[j];
                                    comment.key = commentKeys[j];
                                    commentsArray.push(comment);
                                }
                            }

                            let componentObject = {
                                key: componentKeys[i],
                                row: componentValues[i].row,
                                type: componentValues[i].type,
                                fileType: componentValues[i].fileType,
                                text: componentValues[i].text,
                                linkKey: componentValues[i].linkKey,
                                addedBy: componentValues[i].addedBy,
                                addedByName: componentValues[i].addedByName,
                                addedOn: componentValues[i].addedOn,
                                allowComments: componentValues[i].allowComments,
                                comments: commentsArray,
                            };

                            componentsArray.push(componentObject);
                        }
                    }

                    let project = {
                        key: key,
                        components: componentsArray.sort((a, b) =>
                            a.row > b.row ? 1 : -1
                        ),
                    };

                    // console.log(project);
                    setProject(project);
                });
            } else {
                console.log("Already Read Project");
            }
        },
        [project]
    );
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);

        const key = urlParams.get("key");

        let matchingInfo = projectInfo.find((p) => p.key === key);

        if (
            matchingInfo &&
            currentUserData &&
            groupsLoaded &&
            projectInfoLoaded
        ) {
            let hasAccess = false;

            // Check if user created project

            if (matchingInfo.createdBy === currentUserData.key) {
                hasAccess = true;
                setUserWasCreator(true);
            } else {
                setUserWasCreator(false);
            }

            // Check if user was added to project individually
            let matchingUser = matchingInfo.users.find(
                (u) => u.key === currentUserData.key
            );

            if (matchingUser) {
                if (matchingUser.canEdit) {
                    hasAccess = true;
                }
            }

            // Check if user is in a group that was added to the project
            matchingInfo.groups.forEach((projectGroup) => {
                let matchingGroup = groups.find(
                    (g) => g.key === projectGroup.key
                );
                if (matchingGroup) {
                    if (
                        matchingGroup.users.filter(
                            (u) => u.key === currentUserData.key
                        ).length > 0
                    ) {
                        if (projectGroup.canEdit) {
                            hasAccess = true;
                        }
                    }
                }
            });

            if (hasAccess) {
                // if (currentUserData.permissions.createSurveys) {
                //     setCanShareProjects(true);
                // }

                setCanShareProjects(true); // TEMP!!

                setProjectInfoObject(matchingInfo);
                setKey(matchingInfo.key);
                setTitle(matchingInfo.title);
                setIntroduction(matchingInfo.intro);
                getProject(key);
                setUserHasAccess(true);
            } else {
                setUserHasAccess(false);
            }

            if (project) {
                console.log("Have read project succesfully");
            }
            setAccessResolved(true);
        } else if (currentUserData && groupsLoaded && projectInfoLoaded) {
            setAccessResolved(true);
        }

        // eslint-disable-next-line
    }, [currentUserData, projectInfo, project, projectInfoLoaded]);

    function handleTextFieldChange(event) {
        let newValue = event.target.value;

        if (event.target.id === "projectTitle") {
            // Change in firebase
            firebase
                .database()
                .ref("projectinfo")
                .child(project.key)
                .child("title")
                .set(newValue);

            setTitle(newValue);
        } else if (event.target.id === "projectIntro") {
            // Change in firebase
            firebase
                .database()
                .ref("projectinfo")
                .child(project.key)
                .child("intro")
                .set(newValue);

            setIntroduction(newValue);
        }
    }

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleAddComponentClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    function handleShareProjectClicked() {
        setShareDialogOpen(true);
    }

    // The dialog reports the outcome itself, in one message that covers both who
    // it was shared with and what was delivered.
    function handleCloseShareDialog() {
        setShareDialogOpen(false);
    }

    function handleDeleteProjectPressed() {
        setDeleteConfirmDialogIsOpen(true);
    }

    function handleCloseConfirmDeleteDialog(result) {
        if (result === true) {
            // Confirmed that we want to delete the project
            // Delete project Info Object
            firebase
                .database()
                .ref("projectinfo")
                .child(projectInfoObject.key)
                .remove();

            // Delete Project Object
            firebase
                .database()
                .ref("projects")
                .child(projectInfoObject.key)
                .remove();

            // Delete Responses

            history.replace("/projects/s");
        }

        setDeleteConfirmDialogIsOpen(false);
    }

    function handleComponentMoveUpPressed(component) {
        if (component.row === 0) {
            return;
        }

        let previousComponent = project.components[component.row - 1];

        firebase
            .database()
            .ref("projects")
            .child(key)
            .child("components")
            .child(previousComponent.key)
            .update({ row: component.row });

        firebase
            .database()
            .ref("projects")
            .child(key)
            .child("components")
            .child(component.key)
            .update({ row: component.row - 1 });
    }

    function handleComponentMoveDownPressed(component) {
        if (component.row === project.components.length - 1) {
            return;
        }

        let nextComponent = project.components[component.row + 1];

        firebase
            .database()
            .ref("projects")
            .child(key)
            .child("components")
            .child(nextComponent.key)
            .update({ row: component.row });

        firebase
            .database()
            .ref("projects")
            .child(key)
            .child("components")
            .child(component.key)
            .update({ row: component.row + 1 });
    }

    function handleDeleteComponentPressed(component) {
        // First make sure the row values for each of the remaining components is correct
        var index = 0;
        project.components
            .filter((c) => c.key !== component.key)
            .forEach((component) => {
                firebase
                    .database()
                    .ref("projects")
                    .child(key)
                    .child("components")
                    .child(component.key)
                    .update({ row: index });

                index += 1;
            });

        // Then remove the deleted component
        firebase
            .database()
            .ref("projects")
            .child(key)
            .child("components")
            .child(component.key)
            .remove();
    }

    function handleSelectPlaylistClose(selectedPlaylist) {
        if (selectedPlaylist) {
            let newComponent = {
                type: "Playlist",
                addedBy: currentUserData.key,
                addedByName: currentUserData.fullName,
                addedOn: dayjs().toString(),
                linkKey: selectedPlaylist.key,
                row: project.components.length,
            };

            project.components = [...project.components, newComponent];

            // Write new component to firebase

            let componentsRef = firebase
                .database()
                .ref("projects/" + key + "/components")
                .push();

            componentsRef.set(newComponent);

            forceUpdate();
        }

        setSelectPlaylistDialogOpen(false);
    }

    function handleSelectExistingFileClose(selectedFile) {
        if (selectedFile) {
            let newComponent = {
                type: "File",
                fileType: selectedFile.type,
                addedBy: currentUserData.key,
                addedByName: currentUserData.fullName,
                addedOn: dayjs().toString(),
                linkKey: selectedFile.key,
                row: project.components.length,
            };

            project.components = [...project.components, newComponent];

            // Write new component to firebase

            let componentsRef = firebase
                .database()
                .ref("projects/" + key + "/components")
                .push();

            componentsRef.set(newComponent);

            forceUpdate();
        }

        setSelectExistingFileDialogOpen(false);
    }

    function handleNewTitlePressed() {
        let newComponent = {
            type: "Title",
            text: "",
            addedBy: currentUserData.key,
            addedByName: currentUserData.fullName,
            addedOn: dayjs().toString(),
            //linkKey: "",
            row: project.components.length,
        };

        project.components = [...project.components, newComponent];

        // Write new component to firebase

        let componentsRef = firebase
            .database()
            .ref("projects/" + key + "/components")
            .push();

        componentsRef.set(newComponent);

        forceUpdate();

        setAnchorEl(null);
    }

    function handleNewTextBoxPressed() {
        let newComponent = {
            type: "Textbox",
            text: "",
            addedBy: currentUserData.key,
            addedByName: currentUserData.fullName,
            addedOn: dayjs().toString(),
            allowComments: false,
            row: project.components.length,
        };

        project.components = [...project.components, newComponent];

        // Write new component to firebase

        let componentsRef = firebase
            .database()
            .ref("projects/" + key + "/components")
            .push();

        componentsRef.set(newComponent);

        forceUpdate();

        setAnchorEl(null);
    }

    function handleNewPlaylistPressed() {
        setSelectPlaylistDialogOpen(true);

        setAnchorEl(null);
    }

    function handleNewExistingFilePressed() {
        setSelectExistingFileDialogOpen(true);

        setAnchorEl(null);
    }

    const isLoading =
        !accessResolved || (userHasAccess && !project);

    return (
        <>
            {isLoading && (
                <Box
                    sx={{
                        minHeight: {
                            xs: "calc(100dvh - 60px)",
                            sm: "calc(100vh - 128px)",
                        },
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: "background.default",
                    }}
                >
                    <Box sx={{ textAlign: "center", color: "text.secondary" }}>
                        <CircularProgress
                            size={32}
                            aria-label="Loading project editor"
                        />
                        <Typography variant="body2" sx={{ mt: 1.5 }}>
                            Loading project editor…
                        </Typography>
                    </Box>
                </Box>
            )}

            {accessResolved && !userHasAccess && <NoPermissionsMessage />}

            {userHasAccess && project && (
                <Box
                    component="main"
                    sx={{
                        minHeight: {
                            xs: "calc(100dvh - 60px)",
                            sm: "calc(100vh - 128px)",
                        },
                        pb: 12,
                        backgroundColor: "background.default",
                    }}
                >
                    <Container
                        maxWidth="lg"
                        sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 3,
                            }}
                        >
                            <Card variant="outlined" sx={surfaceSx}>
                                <CardContent
                                    sx={{
                                        p: { xs: 2, sm: 3 },
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        "&:last-child": {
                                            pb: { xs: 2, sm: 3 },
                                        },
                                    }}
                                >
                                    <IconButton
                                        aria-label="Back to projects"
                                        onClick={() => history.goBack()}
                                        sx={{
                                            flexShrink: 0,
                                            border: "1px solid",
                                            borderColor: "divider",
                                        }}
                                    >
                                        <ArrowBackIcon />
                                    </IconButton>
                                    <Box
                                        sx={{
                                            width: 46,
                                            height: 46,
                                            display: {
                                                xs: "none",
                                                sm: "grid",
                                            },
                                            placeItems: "center",
                                            flexShrink: 0,
                                            borderRadius: 2,
                                            color: "primary.main",
                                            backgroundColor:
                                                "rgba(0, 149, 61, 0.09)",
                                        }}
                                    >
                                        <FolderOutlinedIcon />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="h5"
                                            component="h1"
                                            sx={{ lineHeight: 1.1 }}
                                        >
                                            Edit project
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            noWrap
                                            sx={{ mt: 0.5 }}
                                        >
                                            {title || "Untitled project"}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        size="small"
                                        label={`${project.components.length} ${
                                            project.components.length === 1
                                                ? "item"
                                                : "items"
                                        }`}
                                        sx={{
                                            display: {
                                                xs: "none",
                                                md: "flex",
                                            },
                                            backgroundColor:
                                                "rgba(0, 149, 61, 0.09)",
                                            color: "primary.main",
                                        }}
                                    />
                                    {canShareProjects && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<ShareOutlinedIcon />}
                                            onClick={
                                                handleShareProjectClicked
                                            }
                                        >
                                            Share
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "minmax(0, 1fr)",
                                        md: "minmax(0, 1fr) 320px",
                                    },
                                    alignItems: "stretch",
                                    gap: 3,
                                }}
                            >
                                <Card
                                    variant="outlined"
                                    sx={{
                                        ...surfaceSx,
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.25 }}>
                                        <Typography variant="h6" component="h2">
                                            Project details
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.35 }}
                                        >
                                            Give collaborators context for the
                                            content below.
                                        </Typography>
                                    </Box>
                                    <Divider />
                                    <CardContent
                                        sx={{
                                            p: { xs: 2, sm: 3 },
                                            display: "flex",
                                            flex: 1,
                                            flexDirection: "column",
                                            gap: 2,
                                            "&:last-child": {
                                                pb: { xs: 2, sm: 3 },
                                            },
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            value={title}
                                            onChange={handleTextFieldChange}
                                            id="projectTitle"
                                            label="Project title"
                                        />
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={4}
                                            value={introduction}
                                            onChange={handleTextFieldChange}
                                            id="projectIntro"
                                            label="Description"
                                        />
                                    </CardContent>
                                </Card>

                                <Card
                                    variant="outlined"
                                    sx={{
                                        ...surfaceSx,
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <Box sx={{ px: 2.5, py: 2.25 }}>
                                        <Typography variant="h6" component="h2">
                                            Project settings
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.35 }}
                                        >
                                            Changes are saved automatically.
                                        </Typography>
                                    </Box>
                                    <Divider />
                                    <CardContent
                                        sx={{
                                            p: 2.5,
                                            display: "flex",
                                            flex: 1,
                                            flexDirection: "column",
                                            gap: 2,
                                            "&:last-child": { pb: 2.5 },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 1.25,
                                                p: 1.5,
                                                borderRadius: 2,
                                                color: "text.secondary",
                                                backgroundColor: "background.subtle",
                                            }}
                                        >
                                            <AutoAwesomeOutlinedIcon
                                                color="primary"
                                                fontSize="small"
                                            />
                                            <Typography variant="body2">
                                                Add and reorder content below.
                                                Everyone with access sees your
                                                changes as they are saved.
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: 1 }} />
                                        {userWasCreator && (
                                            <Button
                                                color="error"
                                                variant="outlined"
                                                startIcon={
                                                    <DeleteOutlineIcon />
                                                }
                                                onClick={
                                                    handleDeleteProjectPressed
                                                }
                                            >
                                                Delete project
                                            </Button>
                                        )}
                                    </CardContent>
                                </Card>
                            </Box>

                            <Card variant="outlined" sx={surfaceSx}>
                                <Box
                                    sx={{
                                        px: { xs: 2, sm: 3 },
                                        py: 2.25,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                    }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="h6" component="h2">
                                            Project content
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.35 }}
                                        >
                                            Build the project with titles,
                                            text, playlists, and resources.
                                        </Typography>
                                    </Box>
                                </Box>
                            </Card>

                            {project.components.length === 0 ? (
                                <Card variant="outlined" sx={surfaceSx}>
                                    <Box
                                        sx={{
                                            minHeight: 220,
                                            px: 3,
                                            py: 5,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textAlign: "center",
                                            color: "text.secondary",
                                        }}
                                    >
                                        <FolderOutlinedIcon
                                            sx={{
                                                fontSize: 44,
                                                opacity: 0.42,
                                            }}
                                        />
                                        <Typography
                                            variant="h6"
                                            color="text.primary"
                                            sx={{ mt: 1 }}
                                        >
                                            Start building your project
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ mt: 0.5 }}
                                        >
                                            Add a title, text, playlist, or
                                            resource.
                                        </Typography>
                                        <Button
                                            sx={{ mt: 2 }}
                                            variant="contained"
                                            disableElevation
                                            startIcon={<AddIcon />}
                                            onClick={handleAddComponentClick}
                                        >
                                            Add content
                                        </Button>
                                    </Box>
                                </Card>
                            ) : (
                                project.components.map((component) => (
                                    <React.Fragment key={component.key}>
                                        {component.type === "Title" && (
                                            <ProjectTitleComponent
                                                projectKey={project.key}
                                                isEditing
                                                component={component}
                                                moveUp={
                                                    handleComponentMoveUpPressed
                                                }
                                                moveDown={
                                                    handleComponentMoveDownPressed
                                                }
                                                delete={
                                                    handleDeleteComponentPressed
                                                }
                                            />
                                        )}
                                        {component.type === "Textbox" && (
                                            <ProjectTextBoxComponent
                                                projectKey={project.key}
                                                isEditing
                                                component={component}
                                                moveUp={
                                                    handleComponentMoveUpPressed
                                                }
                                                moveDown={
                                                    handleComponentMoveDownPressed
                                                }
                                                delete={
                                                    handleDeleteComponentPressed
                                                }
                                            />
                                        )}
                                        {component.type === "Playlist" && (
                                            <ProjectPlaylistComponent
                                                projectKey={project.key}
                                                projectCreatedBy={
                                                    projectInfoObject?.createdBy
                                                }
                                                isEditing
                                                component={component}
                                                moveUp={
                                                    handleComponentMoveUpPressed
                                                }
                                                moveDown={
                                                    handleComponentMoveDownPressed
                                                }
                                                delete={
                                                    handleDeleteComponentPressed
                                                }
                                            />
                                        )}
                                        {component.type === "File" && (
                                            <ProjectFileComponent
                                                projectKey={project.key}
                                                isEditing
                                                component={component}
                                                moveUp={
                                                    handleComponentMoveUpPressed
                                                }
                                                moveDown={
                                                    handleComponentMoveDownPressed
                                                }
                                                delete={
                                                    handleDeleteComponentPressed
                                                }
                                            />
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </Box>
                    </Container>
                </Box>
            )}

            {userHasAccess && project && (
                <FloatingAddContentButton
                    id="add-project-content-button"
                    onClick={handleAddComponentClick}
                />
            )}

            {deleteConfirmDialogIsOpen && (
                <ConfirmDialog
                    open={deleteConfirmDialogIsOpen}
                    onClose={handleCloseConfirmDeleteDialog}
                    title={"Confirm Delete"}
                    message={"Are you sure you want to delete this project?"}
                />
            )}
            {selectPlaylistDialogOpen && (
                <ProjectSelectPlaylistDialog
                    open={selectPlaylistDialogOpen}
                    onClose={handleSelectPlaylistClose}
                />
            )}
            {selectExistingFileDialogOpen && (
                <ProjectSelectFileDialog
                    open={selectExistingFileDialogOpen}
                    onClose={handleSelectExistingFileClose}
                />
            )}
            <Menu
                id="project-content-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                // The trigger sits at the bottom right of the viewport, so the
                // menu has to open upwards from its right edge to stay on screen.
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                slotProps={{
                    paper: {
                        sx: {
                            mb: 1,
                            minWidth: 240,
                            borderRadius: 2,
                            boxShadow: "0 12px 32px rgba(20, 35, 25, 0.16)",
                        },
                    },
                }}
            >
                <MenuItem
                    onClick={handleNewTitlePressed}
                    sx={{ gap: 1.5, py: 1.25 }}
                >
                    <TitleOutlinedIcon color="action" />
                    Add title
                </MenuItem>
                <MenuItem
                    onClick={handleNewTextBoxPressed}
                    sx={{ gap: 1.5, py: 1.25 }}
                >
                    <AutoAwesomeOutlinedIcon color="action" />
                    Add text
                </MenuItem>
                <MenuItem
                    onClick={handleNewPlaylistPressed}
                    sx={{ gap: 1.5, py: 1.25 }}
                >
                    <PlaylistAddOutlinedIcon color="action" />
                    Add playlist
                </MenuItem>
                <MenuItem
                    onClick={handleNewExistingFilePressed}
                    sx={{ gap: 1.5, py: 1.25 }}
                >
                    <InsertDriveFileOutlinedIcon color="action" />
                    Add resource from files
                </MenuItem>
            </Menu>
            {canShareProjects && projectInfoObject && (
                <ShareProjectDialog
                    open={shareDialogOpen}
                    onClose={handleCloseShareDialog}
                    projectInfo={projectInfoObject}
                />
            )}
            <ToolbarWithLogo />
        </>
    );
}
